import type { ArtMetadata, ArtTextStyle, Global } from "../schedule-presets/art-metadata";
import { formatArtValue } from "../schedule-art/format-art-value";
import type { MetadataEventPayload } from "../metadata-history/metadata-events";
import { builderSampleWeek } from "./sample-schedule";
import { defaultElementPosition } from "../schedule-art/layout-coordinates";

export const weekDateLayouts = ["range", "split", "parts"] as const;
export type WeekDateLayout = typeof weekDateLayouts[number];

export function weekDateEntries(metadata: ArtMetadata) {
  return metadata.globals.flatMap((element, index) =>
    element.bind.startsWith("week.") ? [{ element, index, path: `globals.${index}` }] : []);
}

export function weekDateLayout(metadata: ArtMetadata): WeekDateLayout | null {
  const dates = weekDateEntries(metadata);
  if (dates.length === 1 && dates[0]!.element.bind === "week.range") return "range";
  if (dates.length === 2) return "split";
  if (dates.length === 4) return "parts";
  return null;
}

export function weekDateFormats(layout: WeekDateLayout) {
  if (layout === "range") return [["%m/%d – %m/%d"], ["%m/%d to %m/%d"], ["%b %d – %b %d"]];
  if (layout === "split") return [["%m/%d"], ["%b %d"], ["%m月%d日"]];
  return [["%b", "%d"], ["%m", "%d"], ["%m月", "%d日"]];
}

export function weekDateFormatExample(layout: WeekDateLayout, patterns: string[], week = builderSampleWeek()) {
  const [start, end] = [week[0]!, week[6]!];
  if (layout === "range") return formatArtValue([start, end], patterns[0]);
  if (layout === "split") return [start, end].map(date => formatArtValue(date, patterns[0])).join(" · ");
  return [start, end].flatMap(date => patterns.map(pattern => formatArtValue(date, pattern))).join(" · ");
}

export function weekDateSample(element: Global, metadata: ArtMetadata, week = builderSampleWeek()) {
  const start = week[0]!;
  const end = week[6]!;
  const value = element.bind === "week.range" ? [start, end] as [Date, Date]
    : element.bind === "week.end_date" ? end : start;
  return formatArtValue(value, metadata.formats[element.format ?? ""]);
}

export function createWeekDateLayout(metadata: ArtMetadata, layout: WeekDateLayout): MetadataEventPayload {
  const style = "builder_week_dates";
  const range = layout === "range";
  const parts = layout === "parts";
  const styleNames = parts ? ["builder_week_month_text", "builder_week_day_text"] : [style];
  const defaultStyle: ArtTextStyle = metadata.styles[style] ?? { ...metadata.styles.week_range, size: 48, align: "center" };
  const definitions: Record<string, ArtTextStyle> = {};
  styleNames.forEach(name => {
    if (!metadata.styles[name]) definitions[name] = structuredClone(defaultStyle);
  });
  const bindings: Global["bind"][] = range ? ["week.range"] : parts
    ? ["week.start_date", "week.start_date", "week.end_date", "week.end_date"]
    : ["week.start_date", "week.end_date"];
  const formats: Record<string, string> = range ? { builder_week_range: "%m/%d – %m/%d" } : parts
    ? { builder_week_month: "%b", builder_week_day: "%d" }
    : { builder_week_date: "%m/%d" };
  const positions = range ? [960] : parts ? [730, 870, 1050, 1190] : [820, 1100];
  const globals: Global[] = bindings.map((bind, index) => ({
    bind, style: styleNames[parts ? index % 2 : 0]!, position: defaultElementPosition([positions[index]!, 60]),
    format: range ? "builder_week_range" : parts ? index % 2 === 0 ? "builder_week_month" : "builder_week_day" : "builder_week_date",
  }));
  return { type: "week-dates/set", globals, formats, styles: definitions };
}

export function weekDateFormatPatch(metadata: ArtMetadata, layout: WeekDateLayout, patterns: string[]) {
  const formats: Record<string, string> = {};
  weekDateEntries(metadata).forEach(({ element }, index) => {
    if (!element.format) return;
    formats[element.format] = patterns[layout === "parts" ? index % 2 : 0]!;
  });
  return formats;
}

export function weekDateStyleUpdate(metadata: ArtMetadata, index: number, patch: Partial<ArtTextStyle>, field: keyof ArtTextStyle): MetadataEventPayload | null {
  const dates = weekDateEntries(metadata);
  const ordinal = dates.findIndex(date => date.index === index);
  const element = metadata.globals[index];
  const current = element ? metadata.styles[element.style] : undefined;
  if (ordinal < 0 || !current) return null;
  const parts = weekDateLayout(metadata) === "parts";
  const month = ordinal % 2 === 0;
  const name = parts ? month ? "builder_week_month_text" : "builder_week_day_text" : "builder_week_dates";
  const group = parts ? dates.filter((_, number) => number % 2 === ordinal % 2) : dates;
  const globalIndices = group.map(date => date.index);
  const style = { ...current, ...patch };
  if (!style.stroke) delete style.stroke;
  // Assign both group members and update their definition in one undoable action.
  // This also groups dates from sessions created before month/day styles were split.
  return { type: "style/set", name, style, field, globalIndices };
}
